// Generated by GitHub Copilot
import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Form, Button, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import SimpleMenu from "../SimpleMenu";
import { ComponentAPI } from "../../connections/fetchStatusPageComponents";
import { BrandsAPI } from "../../connections/Brands";
import { CommunicationAPI } from "../../connections/communication";
// Generated by GitHub Copilot
import StatusPageSelector from "../StatusPageSelector";
import { authFetch } from "../../utils/authFetch";
import LeftNavigation from "../LeftNavigation/LeftNavigation";
import {
  buildSpeedyProblemRecord,
  buildSpeedyAdvisoryPayload,
  buildSpeedyPreviewContent,
  sentenceCase,
} from "./speedyPayload";

import "./SpeedyForm.css";

// US2085181: ceiling on how long the deferred advisory send may hold the
// preview hostage. `attachCommunication` reaches StatusPage through
// authFetch, and neither has a timeout, an AbortController, or an
// AbortSignal anywhere in it -- so a proxy holding the socket open is enough
// to make the send never settle. Both preview buttons are disabled={loading}
// and `loading` is held through the awaited settle, so without this ceiling
// the user is stranded on a spinner with no in-page exit: a fresh instance of
// exactly the silent dead-end AC1 exists to eliminate.
//
// Raced here rather than pushed down as AbortSignal.timeout() on
// attachCommunication, deliberately: AbortSignal.timeout() schedules on the
// platform timer, which jest.useFakeTimers() does not patch, so
// advanceTimersByTime could never fire it -- and the suites mock
// connections/communication wholesale, putting any timeout inside that module
// below the mock boundary. The push-down is the conceptually smaller change
// and would cover every caller of that connection, but it would ship
// untestable. Revisit only alongside a testable abort seam in authFetch.
const ADVISORY_SEND_TIMEOUT_MS = 15000;

const SpeedyForm = ({ user, configuration, newrelic }) => {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    prbNumber: "",
    currentStatus: "",
    applicationsImpacted: [],
    brandImpacted: [],
  });

  // Generated by GitHub Copilot
  // Status page targets - separate from formData for easier management
  const [publishingTargets, setPublishingTargets] = useState([]);

  // Configuration and options state
  const [productOptions, setProductOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  // Generated by GitHub Copilot
  // Removed componentOptions and setComponentOptions - now handled by StatusPageSelector
  const [loading, setLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(true);

  // UI state
  const [error, setError] = useState("");
  // US2085181: react-bootstrap variant for the Alert that renders `error`.
  // "danger" when creation itself failed. "warning" when the record WAS created
  // and only the advisory communication failed -- red overstates that, since
  // the thing the user asked for exists.
  const [errorVariant, setErrorVariant] = useState("danger");
  // US2085181: a number that exists in Problem Notes and that the Alert can
  // therefore offer to open. Two sources, one meaning: the number the server
  // rejected as already existing (409), or the number just created whose
  // advisory then failed. Whoever raised the failure owns the message; this
  // holds only what the client needs to build the affordance.
  const [existingRecordNumber, setExistingRecordNumber] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  // Generated by GitHub Copilot
  const [showPreview, setShowPreview] = useState(false);

  // Generated by GitHub Copilot
  // Use centralized configuration instead of fetching products locally
  const fetchProductOptions = useCallback(() => {
    if (configuration && configuration.products) {
      setProductOptions(configuration.products);
    } else {
      console.warn("No products available in centralized configuration");
      setProductOptions([]);
    }
  }, [configuration]);

  // US1929214: Brand dropdown now sourced from BrandsAPI instead of StatusPage groups
  const fetchBrandOptions = useCallback(() => {
    BrandsAPI.get()
      .then((result) => {
        const brands = (result && result.brands) || [];
        setBrandOptions(brands.map((b) => ({ label: b.name, value: b.id })));
      })
      .catch((error) => {
        console.error("Error fetching brands:", error);
        setBrandOptions([]);
      });
  }, []);

  const fetchStatusPageGroups = useCallback(() => {
    ComponentAPI.getGroups()
      .then((groups) => {
        const groupOptions = groups.map((g) => {
          return { label: g.name, value: g.id };
        });
        setGroupOptions(groupOptions);
      })
      .catch((error) => {
        console.error("Error fetching status page groups:", error);
      });
  }, []);

  // Generated by GitHub Copilot
  // Removed loadComponents function - now handled by StatusPageSelector component

  // Load all configuration data on component mount
  useEffect(() => {
    setOptionsLoading(true);

    Promise.all([
      fetchProductOptions(),
      fetchBrandOptions(),
      fetchStatusPageGroups(),
    ]).finally(() => {
      setOptionsLoading(false);
    });
  }, [fetchProductOptions, fetchBrandOptions, fetchStatusPageGroups]);

  // Generated by GitHub Copilot
  // Removed useEffect for loading components based on statusPageGroup
  // This is now handled internally by StatusPageSelector component

  // Form validation
  const validateForm = () => {
    const errors = {};

    if (!formData.title.trim()) {
      errors.title = "Title is required";
    }

    if (!formData.currentStatus.trim()) {
      errors.currentStatus = "Current status is required";
    }

    if (formData.applicationsImpacted.length === 0) {
      errors.applicationsImpacted = "At least one application must be selected";
    }

    if (formData.brandImpacted.length === 0) {
      errors.brandImpacted = "At least one brand must be selected";
    }

    // Generated by GitHub Copilot
    // Updated validation to check for at least one publishing target
    if (publishingTargets.length === 0) {
      errors.publishingTargets =
        "At least one status page group and component must be selected";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle dropdown changes
  const handleDropdownChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error when user makes selection
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Reset the whole alert -- message, variant, and open-existing affordance --
  // so a control left over from a previous attempt can never outlive it.
  const clearAlert = () => {
    setError("");
    setErrorVariant("danger");
    setExistingRecordNumber("");
  };

  // US2085181: adopt the number the server actually created into form state.
  // This is the double-create guard, and it is the whole reason it exists.
  // On the auto-mint path the user submits no number, so `prbNumber` is empty;
  // if the advisory then fails or times out the form comes back populated with
  // Preview re-enabled, and a second Confirm would POST no number again, sail
  // past apiPostProblem's duplicate check, and mint a SECOND record with
  // identical content. Backfilling makes that retry carry a number the server
  // can reject with a 409. Written as a functional update so it cannot clobber
  // a concurrent field edit.
  const backfillCreatedNumber = (createdProblem) => {
    setFormData((prev) => ({
      ...prev,
      prbNumber: createdProblem?.number || prev.prbNumber,
    }));
  };

  // Handle form submission for preview
  const handleSubmit = async () => {
    // US2085181: clear before validating, not after. A failed validation
    // returns early, so clearing below the guard let a previous attempt's
    // amber warning -- and its live "Open PRB..." Button -- survive next to
    // the fresh red field errors, offering to open a record the form no
    // longer describes. Every invocation must retire the old alert.
    clearAlert();

    if (!validateForm()) {
      return;
    }

    // Generated by GitHub Copilot
    // Show preview instead of immediately creating the problem
    setShowPreview(true);
  };

  // Generated by GitHub Copilot
  // Create problem record and send the advisory communication
  const createProblemRecord = async () => {
    // Create problem record structure matching API expectations
    const problemRecord = buildSpeedyProblemRecord(formData);

    // Make API call to create problem
    const response = await authFetch("/api/v1/problem", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        problem: problemRecord,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const failure = new Error(
        sentenceCase(errorData.error) ||
          `Failed to create problem: ${response.status} ${response.statusText}`
      );
      // US2085181: a 409 means this number already has a Problem Notes record.
      // Carry the submitted number out with the error so the Alert can link to
      // the existing record. The message itself stays exactly as the server
      // wrote it -- do not substitute copy per status code here.
      if (response.status === 409) {
        failure.duplicateNumber = problemRecord.number;
      }
      throw failure;
    }

    const result = await response.json();
    const createdProblem = result.problem;

    // US2085181: the 1000ms delay hedges S3 read-after-write on
    // ListObjectVersions -- the advisory attach can 404 on a problem that was
    // just written. That delay stays.
    //
    // Invariant: this is AWAITED, not fire-and-forget, so handleConfirmCreate's
    // `finally { setLoading(false) }` cannot run until the deferred work has
    // settled, timed out, navigated, or rendered an Alert. `loading` is what
    // disables Confirm, so returning early re-enabled it for a full second with
    // the preview still up and nothing visible having happened. A second click
    // then re-POSTs -- and that is only safe against a silent double-create
    // because every terminal branch below backfills `prbNumber` with the
    // created number, so the retry carries a number the server can 409 on. That
    // holds unconditionally: the advisory send has no opt-out parameter, so
    // there is no caller-selectable path that skips the backfill. On
    // the auto-mint path (Speedy Form submits no `number` and the backend mints
    // a PRBT) `prbNumber` would otherwise still be empty, the duplicate guard
    // could not fire, and a second PRBT would be minted with the same content.
    //
    // Accepted limitation: the timer has no unmount cleanup, so a user who
    // leaves via the left nav inside the window is told nothing. The
    // occurrence is still counted either way -- but by a different set of page
    // actions per ordering, and none of them is inferable from the others.
    // Enumerated below rather than summarized as a property, because every
    // attempt to state this as a property has been wrong: a stalled send is
    // NOT cancelled (see the timedOut note below) and therefore can settle
    // minutes later, which is exactly the case a one-line summary keeps
    // dropping. The four signatures, verbatim:
    //
    //   clean success           Speedy.Problem.Created + Communication.Sent
    //   reject BEFORE the       Speedy.Problem.Created + Communication.Failed
    //     ceiling               (+ noticeError)
    //   stall that NEVER        Speedy.Problem.Created + Communication.TimedOut
    //     settles
    //   stall that settles      Speedy.Problem.Created + Communication.TimedOut,
    //     AFTER the ceiling     then a late Communication.Sent OR a late
    //                           Communication.Failed (+ noticeError in the
    //                           reject case)
    //
    // Two consequences worth stating outright. First, every ordering fires
    // Speedy.Problem.Created, because the timeout branch settles the outer
    // promise rather than throwing -- so a lone Created is not evidence the
    // advisory went out. Second, Communication.Sent and Communication.Failed
    // are therefore NOT a partition of the outcome space: the fourth row
    // increments TimedOut and then one of them for the SAME attempt, so the
    // counts overlap, Sent is not a clean-success counter, and anyone deriving
    // a success rate has to account for the late-settle double count.
    //
    // The late noticeError in row four is real and intended. The timedOut flag
    // guards only the OUTER .then()/.catch() below -- navigate() and the
    // setters -- so sendAdvisoryCommunication's own catch is unguarded and
    // still fires noticeError + Communication.Failed on a post-ceiling reject.
    // Leave it that way: a send that ultimately rejects IS a genuine
    // communication failure and belongs in the error rate. Do NOT extend
    // timedOut inward to suppress that catch; that would hide a real failure
    // for the sake of a tidier comment. See docs/LORE.md ("deferred rejection
    // dead-end").
    await new Promise((settle) => {
      setTimeout(() => {
        // US2085181: `timedOut` makes this block single-settle. The raced-out
        // send is NOT cancelled -- there is no AbortController anywhere in
        // authFetch/communication -- so a stalled request can still resolve
        // minutes later. Without this flag its `.then()` would call navigate()
        // and yank the user off the form they were just warned on, and its
        // `.catch()` would overwrite the timeout warning. Both handlers are
        // therefore no-ops once the timeout has fired.
        let timedOut = false;

        let timeoutID;
        const timeout = new Promise((resolve) => {
          timeoutID = setTimeout(() => {
            timedOut = true;
            resolve("timeout");
          }, ADVISORY_SEND_TIMEOUT_MS);
        });

        Promise.race([
          sendAdvisoryCommunication(createdProblem)
            .then(() => {
              if (timedOut) return;
              navigate(`/problem/edit/${createdProblem.number}`);
            })
            .catch((err) => {
              if (timedOut) return;
              // US2085181: this callback runs on a later tick, so
              // handleConfirmCreate's try/catch can never see this rejection
              // -- it needs its own terminal handler. The record WAS created,
              // so this is a warning, not a failure, and it must NOT
              // auto-navigate: that unmounts the form and discards the
              // warning. Setter order and the optional chaining are
              // defensive, not load-bearing.
              // See docs/LORE.md ("deferred rejection dead-end").
              setShowPreview(false); // the Alert + Button live on the form view
              setError(
                err?.message ||
                  "Advisory communication failed but problem was created successfully"
              );
              setErrorVariant("warning");
              setExistingRecordNumber(createdProblem?.number || "");
              backfillCreatedNumber(createdProblem);
            }),
          timeout,
        ])
          .then((outcome) => {
            if (outcome !== "timeout") return;
            // US2085181 AC3: count the ceiling firing BEFORE the setters, not
            // after. This branch settles the outer promise rather than
            // throwing, so createProblemRecord returns normally and
            // handleConfirmCreate fires Speedy.Problem.Created exactly as on a
            // clean success; Speedy.Problem.CreateFailed only fires when
            // createProblemRecord throws, which this path never does, and
            // Communication.Failed lives in sendAdvisoryCommunication's own
            // catch, which the stall has NOT reached at the instant the ceiling
            // fires. Without this line a stalled advisory emits ONE page action
            // at the ceiling and it is the success event. Telemetry-first
            // because the trailing .catch() below deliberately swallows a throw
            // from any setter -- ordering it last would make the count depend
            // on the setters succeeding.
            //
            // No noticeError on THIS line: the record exists, this is the
            // warning path, and status/error-rate inflation for an expected
            // outcome is exactly what CLAUDE.md forbids. That is a statement
            // about this line only -- NOT a claim that the path never touches
            // the error rate. The raced-out send is still live, and if it
            // rejects later, sendAdvisoryCommunication's own catch fires
            // noticeError + Communication.Failed after this TimedOut. That is
            // the correct outcome for a send that genuinely failed; see the
            // four-signature table above createProblemRecord's timer.
            newrelic?.addPageAction("Communication.TimedOut", {
              problemNumber: createdProblem?.number,
              timeoutMs: ADVISORY_SEND_TIMEOUT_MS,
            });
            // The send never settled. Reuse the warning path rather than
            // inventing a second error surface: the record exists, so red
            // overstates it, and the user must never be left on a dead
            // spinner with both preview buttons disabled={loading}. This copy
            // is client-authored because no server message exists for a
            // client-side timeout -- that is not a breach of
            // server-owns-the-message, which governs server responses.
            // Deliberately does NOT navigate: navigating would unmount the
            // form and discard this very warning.
            setShowPreview(false);
            setError(
              "The advisory communication is taking longer than expected and may not have been sent. " +
                "The problem record was created successfully -- open it to check and resend if needed."
            );
            setErrorVariant("warning");
            setExistingRecordNumber(createdProblem?.number || "");
            backfillCreatedNumber(createdProblem);
          })
          .finally(() => {
            // Clear the loser so a settled send does not leave a 15s timer
            // pending -- which would otherwise dangle past unmount, and, in
            // tests, be fired by an afterEach timer drain.
            clearTimeout(timeoutID);
            // `settle` is `resolve` from `new Promise`, which is idempotent by
            // spec, and a `.finally` callback runs at most once per
            // settlement -- so no re-entry guard is needed here.
            settle();
          })
          .catch(() => {
            // US2085181: terminal handler for a throw from one of the
            // setters above -- an unhandled rejection here can fail an
            // unrelated Jest suite under `--ci`. It does NOT swallow the
            // advisory error, which the `.catch()` on the send handles
            // above, and there is no dead spinner either way because
            // settle() has already run from `.finally`.
          });
      }, 1000);
    });

    return createdProblem;
  };

  // Generated by GitHub Copilot
  // Send automatic advisory communication for the created problem
  const sendAdvisoryCommunication = async (problem) => {
    try {
      // Generated by GitHub Copilot
      // Retrieve sender information from localStorage (same pattern as ProblemForm)
      let senderName = "Generic MIM";
      let senderEmail = "coxautomim@coxautoinc.com";
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          senderName = user.name || "Generic MIM";
          senderEmail = user.email || "coxautomim@coxautoinc.com";
        } catch (error) {
          console.error("Error parsing user from localStorage:", error);
        }
      }

      // Create advisory communication payload
      const advisoryPayload = buildSpeedyAdvisoryPayload(
        formData,
        {
          name: senderName,
          email: senderEmail,
        },
        publishingTargets
      );

      // Generated by GitHub Copilot
      // Send advisory communication using CommunicationAPI for consistent error handling
      await CommunicationAPI.attachCommunication(
        problem.number,
        "OUTAGE 1",
        advisoryPayload
      );

      newrelic?.addPageAction("Communication.Sent", {
        problemNumber: problem.number,
      });
    } catch (err) {
      newrelic?.noticeError(err);
      newrelic?.addPageAction("Communication.Failed", {
        // US2085181: optional-chained defensively against a malformed
        // response -- a future or intermediary source returning 2xx with no
        // `problem` in the body. Unguarded, this line throws before the
        // intended message below is ever constructed and the user sees a raw
        // TypeError instead. No live producer of that shape remains: the
        // pre-fix `208` was the only one, and `apiPostProblem` cannot emit it
        // (it would panic dereferencing `*problem` first). So the un-backfilled
        // `prbNumber` on this path -- `createdProblem?.number || prev.prbNumber`
        // is a no-op when `createdProblem` is undefined, which would let a
        // retry double-create -- is unreachable rather than merely unlikely.
        problemNumber: problem?.number,
        errorMessage: err?.message,
      });
      throw new Error(
        "Advisory communication failed but problem was created successfully",
        { cause: err }
      );
    }
  };

  const handleCancel = () => {
    navigate("/");
  };

  // Generated by GitHub Copilot
  // Handle actual problem creation after preview confirmation
  const handleConfirmCreate = async () => {
    setLoading(true);
    clearAlert();

    try {
      // Create problem record with advisory communication
      const createdProblem = await createProblemRecord();
      newrelic?.addPageAction("Speedy.Problem.Created", {
        problemNumber: createdProblem?.number,
      });
    } catch (err) {
      console.error("Error creating problem:", err);
      newrelic?.noticeError(err);
      newrelic?.addPageAction("Speedy.Problem.CreateFailed", {
        problemNumber: formData.prbNumber?.trim() || null,
        errorMessage: err?.message,
      });
      // Surface the server's specific reason when present (e.g. an INC that
      // isn't an existing EOC Bridge incident) rather than a generic message.
      setError(err?.message || "Failed to create problem. Please try again.");
      setExistingRecordNumber(err?.duplicateNumber || "");
      setShowPreview(false); // Return to form on error
    } finally {
      setLoading(false);
    }
  };

  // Generated by GitHub Copilot
  // Handle going back to edit the form from preview
  const handleBackToEdit = () => {
    setShowPreview(false);
    // Defense-in-depth, not reachable today -- reachability trace and why it
    // stays in docs/LORE.md (US2085181).
    clearAlert();
  };

  // Generated by GitHub Copilot
  // Render preview content
  const renderPreview = () => {
    const previewContent = buildSpeedyPreviewContent(
      formData,
      publishingTargets
    );

    return (
      <Container
        className="speedy-form-container px-0"
        style={{ marginLeft: "-15px" }}
      >
        <Row>
          <Col
            xs={12}
            sm={4}
            md={3}
            lg={3}
            xl={2}
            className="d-none d-sm-block"
          >
            <LeftNavigation user={user} newrelic={newrelic} />
          </Col>
          <Col xs={12} sm={8} md={9} lg={9} xl={10}>
            <Row>
              <Col>
                <h2>Preview - Advisory Communication</h2>
                <p className="text-muted">
                  Review the advisory communication that will be sent before
                  creating the PRB.
                </p>
              </Col>
            </Row>

            {error && (
              <Row>
                <Col>
                  <Alert variant={errorVariant} className="mb-3">
                    {error}
                  </Alert>
                </Col>
              </Row>
            )}

            <Row>
              <Col>
                <div className="preview-card border rounded p-4 bg-light">
                  <h4 className="mb-3">Problem Summary</h4>
                  <Row className="mb-3">
                    <Col md={6}>
                      <strong>PRB:</strong>{" "}
                      <div className="mt-1">
                        {formData.prbNumber?.trim() ? (
                          formData.prbNumber
                        ) : (
                          <span className="text-muted fst-italic">
                            Will be auto-generated
                          </span>
                        )}
                      </div>
                    </Col>
                    <Col>
                      <strong>Title:</strong>
                      <div className="mt-1">{formData.title}</div>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <strong>Current Status:</strong>
                      <div className="mt-1">{formData.currentStatus}</div>
                    </Col>
                  </Row>

                  <h4 className="mb-3 mt-4">Advisory Communication Details</h4>
                  <Row className="mb-3">
                    <Col>
                      <strong>Subject:</strong>
                      <div className="mt-1">{previewContent.subject}</div>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={6}>
                      <strong>Applications Impacted:</strong>
                      <ul className="mt-1 mb-0">
                        {previewContent.applications.map((app, index) => (
                          <li key={index}>{app}</li>
                        ))}
                      </ul>
                    </Col>
                    <Col md={6}>
                      <strong>Brands Impacted:</strong>
                      <ul className="mt-1 mb-0">
                        {formData.brandImpacted.map((brand, index) => (
                          <li key={index}>{brand.label}</li>
                        ))}
                      </ul>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={6}>
                      <strong>Status Page Targets:</strong>
                      <div className="mt-1">
                        {previewContent.publishingTargets &&
                        previewContent.publishingTargets.length > 0 ? (
                          <ul className="mb-0">
                            {previewContent.publishingTargets.map(
                              (target, index) => (
                                <li key={index}>
                                  {target.group.label} -{" "}
                                  {target.component.label}
                                </li>
                              )
                            )}
                          </ul>
                        ) : (
                          <span className="text-muted">None selected</span>
                        )}
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>

            <Row className="mt-4">
              <Col>
                <Button
                  variant="success"
                  onClick={handleConfirmCreate}
                  disabled={loading}
                  className="me-3"
                >
                  {loading
                    ? "Creating PRB & Sending Advisory..."
                    : "Confirm & Create PRB"}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={handleBackToEdit}
                  disabled={loading}
                >
                  Back to Edit
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    );
  };

  // Generated by GitHub Copilot
  // Render form content
  const renderForm = () => (
    <Container
      className="speedy-form-container px-0"
      style={{ marginLeft: "-15px" }}
    >
      <Row>
        <Col xs={12} sm={4} md={3} lg={3} xl={2} className="d-none d-sm-block">
          <LeftNavigation user={user} newrelic={newrelic} />
        </Col>
        <Col xs={12} sm={8} md={9} lg={9} xl={10}>
          <Row>
            <Col>
              <h2>Create New PRB - Speedy Entry</h2>
              <p className="text-muted">
                Quickly create a new problem record with essential information.
                You'll be taken to the full editing form after creation.
              </p>
            </Col>
          </Row>

          {error && (
            <Row>
              <Col>
                <Alert variant={errorVariant} className="mb-3">
                  {error}
                  {/* US2085181: whenever a record exists behind the message --
                      the 409 duplicate, or a created record whose advisory
                      failed -- give the user the one action they are about to
                      take anyway. A Button driving the already-imported
                      navigate, NOT a react-router Link: SpeedyForm.render.test.js
                      mocks react-router-dom with a factory exposing only
                      useNavigate, so a Link import would be undefined there. */}
                  {existingRecordNumber && (
                    <div className="mt-2">
                      <Button
                        variant={`outline-${errorVariant}`}
                        size="sm"
                        data-testid="speedy-duplicate-open-existing"
                        onClick={() =>
                          navigate(`/problem/edit/${existingRecordNumber}`)
                        }
                      >
                        Open {existingRecordNumber}
                      </Button>
                    </div>
                  )}
                </Alert>
              </Col>
            </Row>
          )}

          <Form>
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Title *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    isInvalid={!!validationErrors.title}
                    placeholder="Enter a descriptive title for this issue..."
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">
                    This will be used as the subject of communications and the
                    short description of the problem record
                  </Form.Text>
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.title}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>PRB or INC Number</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.prbNumber}
                    onChange={(e) =>
                      // Normalize to upper-case: PRB/INC numbers are upper-case and
                      // the backend validators are case-sensitive, so "inc4449471"
                      // and "INC4449471" create the same record (parity with search).
                      handleInputChange("prbNumber", e.target.value.toUpperCase())
                    }
                    placeholder="e.g. PRB1234567 or INC4449471"
                    disabled={loading}
                  />
                  <Form.Text className="text-muted">
                    Leave empty to have a PRB number automatically generated
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Current Status *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={formData.currentStatus}
                    onChange={(e) =>
                      handleInputChange("currentStatus", e.target.value)
                    }
                    isInvalid={!!validationErrors.currentStatus}
                    placeholder="Describe the current status of the issue..."
                    disabled={loading}
                  />
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.currentStatus}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group
                  className="mb-3"
                  data-testid="speedy-applications-impacted-group"
                >
                  <Form.Label>Applications Impacted *</Form.Label>
                  <SimpleMenu
                    multiple={true}
                    options={productOptions}
                    defaultValue={formData.applicationsImpacted}
                    onChange={(value) =>
                      handleDropdownChange("applicationsImpacted", value)
                    }
                    loading={optionsLoading}
                    disabled={loading || optionsLoading}
                  />
                  <Form.Text className="text-muted">
                    Select all applications affected by this issue
                  </Form.Text>
                  {validationErrors.applicationsImpacted && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.applicationsImpacted}
                    </div>
                  )}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group
                  className="mb-3"
                  data-testid="speedy-brand-impacted-group"
                >
                  <Form.Label>Brand Impacted *</Form.Label>
                  <SimpleMenu
                    multiple={true}
                    options={brandOptions}
                    defaultValue={formData.brandImpacted}
                    onChange={(value) =>
                      handleDropdownChange("brandImpacted", value)
                    }
                    loading={optionsLoading}
                    disabled={loading || optionsLoading}
                  />
                  <Form.Text className="text-muted">
                    Select all brands affected by this issue
                  </Form.Text>
                  {validationErrors.brandImpacted && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.brandImpacted}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {/* Generated by GitHub Copilot */}
            <Row>
              <Col>
                <Form.Group className="mb-3">
                  <Form.Label>Status Page Targets *</Form.Label>
                  <StatusPageSelector
                    targets={publishingTargets}
                    onTargetsChange={setPublishingTargets}
                    groupOptions={groupOptions}
                    showExistingTargetsLabel={false}
                    showDivider={false}
                    columnSize={6}
                  />
                  <Form.Text className="text-muted">
                    Select status page groups and components that should be
                    notified. You can add multiple targets.
                  </Form.Text>
                  {validationErrors.publishingTargets && (
                    <div className="invalid-feedback d-block">
                      {validationErrors.publishingTargets}
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Row className="mt-4">
              <Col>
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={loading || optionsLoading}
                  className="me-3"
                >
                  Preview Communication
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </Container>
  );

  return showPreview ? renderPreview() : renderForm();
};

export default SpeedyForm;
